import { useEffect } from 'react'
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts'
import clsx from 'clsx'
import { useStore } from '../../store/useStore'
import { formatPriceWithSymbol, OHLCVCandle, getSymbolName } from '../../types'
import { getSignal, SIGNAL_ARROW, SIGNAL_BG_COLOR, SIGNAL_TEXT_COLOR } from '../../utils/signal'

interface Props {
  symbol: string
  expanded?: boolean
  onClick?: () => void
}

function getMarketType(
  symbol: string,
  symbols: { crypto: string[]; stock: string[]; fx: string[] } | null,
): string {
  if (!symbols) return 'unknown'
  if (symbols.crypto.includes(symbol)) return 'crypto'
  if (symbols.stock.includes(symbol)) return 'stock'
  if (symbols.fx.includes(symbol)) return 'fx'
  return 'unknown'
}

const MARKET_BADGE: Record<string, { label: string; cls: string }> = {
  crypto: { label: 'CRYPTO', cls: 'bg-trading-yellow/20 text-trading-yellow border-trading-yellow/30' },
  stock: { label: 'STOCK', cls: 'bg-trading-blue/20 text-trading-blue border-trading-blue/30' },
  fx: { label: 'FX', cls: 'bg-trading-green/20 text-trading-green border-trading-green/30' },
  unknown: { label: '?', cls: 'bg-trading-muted/20 text-trading-muted border-trading-muted/30' },
}

function SparkLine({ candles, height = 64 }: { candles: OHLCVCandle[]; height?: number }) {
  const data = candles.slice(-24).map((c) => ({ close: c.close }))
  if (data.length < 2) return <div style={{ height }} className="flex items-center justify-center text-trading-muted text-xs">—</div>

  const first = data[0].close
  const last = data[data.length - 1].close
  const isUp = last >= first
  const color = isUp ? '#00d4aa' : '#ff4d6d'

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
        <Line
          type="monotone"
          dataKey="close"
          stroke={color}
          strokeWidth={1.5}
          dot={false}
          isAnimationActive={false}
        />
        <Tooltip content={() => null} cursor={false} />
      </LineChart>
    </ResponsiveContainer>
  )
}

function SkeletonCard({ expanded }: { expanded?: boolean }) {
  return (
    <div className={clsx('trading-card animate-pulse', expanded && 'p-6')}>
      <div className="flex items-center justify-between mb-3">
        <div className="skeleton h-5 w-24 rounded" />
        <div className="skeleton h-4 w-12 rounded-full" />
      </div>
      <div className="skeleton h-8 w-32 rounded mb-3" />
      <div className="skeleton h-2 w-full rounded mb-2" />
      <div className="skeleton h-16 w-full rounded" />
    </div>
  )
}

export default function SymbolCard({ symbol, expanded = false, onClick }: Props) {
  const {
    symbols,
    predictions,
    loadingPredictions,
    ohlcv,
    selectedInterval,
    loadPrediction,
    loadOHLCV,
    setSelectedSymbol,
    metadata,
  } = useStore()

  const predictionKey = `${symbol}_${selectedInterval}`
  const isLoading = loadingPredictions[predictionKey] ?? false
  const prediction = predictions[predictionKey] ?? null
  const ohlcvKey = `${symbol}_${selectedInterval}`
  const candles = ohlcv[ohlcvKey] ?? prediction?.ohlcv ?? []
  const marketType = getMarketType(symbol, symbols)
  const badge = MARKET_BADGE[marketType] ?? MARKET_BADGE.unknown

  // Load data on mount if not cached
  useEffect(() => {
    if (!prediction && !isLoading) {
      loadPrediction(symbol, selectedInterval)
    }
    if (!candles.length) {
      loadOHLCV(symbol, selectedInterval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, selectedInterval])

  if (isLoading && !prediction) {
    return <SkeletonCard expanded={expanded} />
  }

  const currentPrice = prediction?.current_price ?? candles[candles.length - 1]?.close ?? 0
  const prevClose = candles.length >= 2 ? candles[candles.length - 2]?.close : undefined
  const dailyChange = prevClose && prevClose !== 0 ? ((currentPrice - prevClose) / prevClose) * 100 : 0
  const isPositive = dailyChange >= 0

  const risePct = prediction?.prediction.rise_probability ?? null
  const predictedVol = prediction?.prediction.predicted_volatility ?? null

  const riseSignal = risePct !== null ? getSignal(risePct) : null
  const riseColor     = riseSignal ? SIGNAL_BG_COLOR[riseSignal]   : 'bg-trading-muted'
  const riseTextColor = riseSignal ? SIGNAL_TEXT_COLOR[riseSignal] : 'text-trading-muted'

  const handleClick = () => {
    if (onClick) {
      onClick()
    } else {
      setSelectedSymbol(symbol)
    }
  }

  return (
    <div
      className={clsx(
        'trading-card cursor-pointer transition-all duration-200 hover:border-trading-blue/40',
        expanded ? 'p-6' : 'p-4',
      )}
      onClick={handleClick}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="flex flex-col">
            <span
              className={clsx(
                'font-bold font-mono text-trading-text',
                expanded ? 'text-xl' : 'text-base',
              )}
            >
              {symbol}
            </span>
            {(metadata[symbol.toUpperCase()] || getSymbolName(symbol)) && (
              <span className="text-[11px] text-trading-muted font-medium mt-0.5 leading-none">
                {metadata[symbol.toUpperCase()] || getSymbolName(symbol)}
              </span>
            )}
          </div>
          {/* Kronos chip */}
          <div className="flex items-center gap-1 mt-0.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-trading-blue animate-pulse" />
            <span className="text-[9px] text-trading-muted uppercase tracking-wider">TradeVista AI</span>
          </div>
        </div>
        <span
          className={clsx(
            'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border',
            badge.cls,
          )}
        >
          {badge.label}
        </span>
      </div>

      {/* Price */}
      <div className="flex items-baseline gap-2 mb-3">
        <span
          className={clsx(
            'font-mono font-semibold tabular-nums',
            expanded ? 'text-3xl' : 'text-xl',
            isPositive ? 'text-trading-green' : 'text-trading-red',
          )}
        >
          {currentPrice > 0 ? formatPriceWithSymbol(currentPrice, symbol) : '—'}
        </span>
        {prevClose !== undefined && (
          <span
            className={clsx(
              'text-xs font-mono tabular-nums',
              isPositive ? 'text-trading-green' : 'text-trading-red',
            )}
          >
            {isPositive ? '+' : ''}{dailyChange.toFixed(2)}%
          </span>
        )}
      </div>

      {/* Rise probability */}
      {risePct !== null && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-trading-muted uppercase tracking-wider">上昇確率</span>
            <span className={clsx('text-xs font-mono font-semibold tabular-nums', riseTextColor)}>
              {riseSignal ? SIGNAL_ARROW[riseSignal] : '→'} {Math.round(risePct * 100)}%
            </span>
          </div>
          <div className="w-full h-1.5 bg-trading-bg rounded-full overflow-hidden">
            <div
              className={clsx('h-full rounded-full transition-all duration-700', riseColor)}
              style={{ width: `${Math.round(risePct * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Predicted volatility */}
      {predictedVol !== null && (
        <div className="mb-3">
          <span className="text-[10px] text-trading-muted uppercase tracking-wider">予測変動率: </span>
          <span className="text-[10px] font-mono text-trading-yellow tabular-nums">
            {(predictedVol * 100).toFixed(2)}%
          </span>
        </div>
      )}

      {/* Sparkline */}
      {candles.length > 0 && (
        <SparkLine candles={candles} height={expanded ? 100 : 64} />
      )}

      {/* Loading overlay for refresh */}
      {isLoading && prediction && (
        <div className="mt-2 flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-trading-blue animate-pulse" />
          <span className="text-[10px] text-trading-muted">更新中...</span>
        </div>
      )}
    </div>
  )
}
